import type { ComponentType, JSX } from 'react';
import { memo } from 'react';

import { withErrorBoundary } from '../ErrorBoundary';
import FilterModal from '../FilterModal';

import type { PkTheme } from '@shared-types/common.types';

import * as classes from './styles.module.scss';

const ListFilterModal = <T extends { id: string }>(props: {
  onSelected: (entities: T[]) => void;
  entities: T[];
  selectedEntities?: T[];
  dialogClassName?: string;
  buttonClassName?: string;
  buttonWidth?: string;
  headerText?: string;
  applyBtnTag?: string;
  resetBtnTag?: string;
  title?: string;
  errorText?: string;
  dialogTheme?: PkTheme;
  headerTheme?: PkTheme;
  multiSelect?: boolean;
  onModalOpen?: () => void;
  ButtonContent: ComponentType<{ entities: T[] }>;
  ItemComponent: ComponentType<{ entity: T; onSelect: (selected: boolean) => void; selected: boolean }>;
  subContentNote?: JSX.Element;
}) => {
  const { onSelected, entities, multiSelect, selectedEntities, title, ItemComponent, ButtonContent, subContentNote } =
    props;

  const renderModal = ({
    localState,
    onContentUpdate,
  }: {
    localState: T[];
    onContentUpdate: (newState: T[] | ((state: T[]) => T[])) => void;
  }) => {
    const handleSelect = (entity: T) => (isSelected: boolean) => {
      if (isSelected) {
        onContentUpdate((state) => (multiSelect ? [...state, entity] : [entity]));
      } else {
        onContentUpdate((state) => state.filter((e) => e.id !== entity.id));
      }
    };
    return (
      <>
        {!!title && (
          <div className='pk-font--base-semibold pk-text--text-01 pk-line-height--md pk-font-size--l pk-mb--m'>
            {title}
          </div>
        )}

        <div className={classes.grid}>
          {entities?.map((entity) => (
            <ItemComponent
              key={`entity-${entity.id}`}
              entity={entity}
              onSelect={handleSelect(entity)}
              selected={localState?.some((e) => e.id === entity.id)}
            />
          ))}
        </div>
      </>
    );
  };

  const renderButton = ({ state }: { state: T[] }) => <ButtonContent entities={state} />;

  return (
    <FilterModal<T[]>
      {...props}
      ButtonContent={renderButton}
      onUpdate={onSelected}
      resetState={[]}
      closeOnReset={!multiSelect}
      stateIsValid={(state) => state?.length > 0}
      initialState={selectedEntities}
      Content={renderModal}
      subContentNote={subContentNote}
    />
  );
};

export default withErrorBoundary(memo(ListFilterModal)) as typeof ListFilterModal;
